import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import allRoutes from 'client/constants/allRoutes';

const SubscriptionBanner = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [bannerType, setBannerType] = useState(null); // 'trial' | 'expiring' | 'grace' | null
  const [graceDaysLeft, setGraceDaysLeft] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const isSuperAdmin = user?.RoleId === 1;

  // Listen for subscription info from API responses
  useEffect(() => {
    const handleApiResponse = (event) => {
      const data = event.detail;
      if (!data) return;

      if (data.grace_period) {
        setBannerType('grace');
        setGraceDaysLeft(data.grace_days_left || 0);
        setDismissed(false);
      } else if (data.trial_info) {
        const daysLeft = data.trial_info.days_left;
        setTrialDaysLeft(daysLeft);
        setBannerType(daysLeft <= 3 ? 'expiring' : 'trial');
        setDismissed(false);
      }
    };

    window.addEventListener('subscription:info', handleApiResponse);
    return () => window.removeEventListener('subscription:info', handleApiResponse);
  }, []);

  if (isSuperAdmin || dismissed || !bannerType) return null;

  const bannerStyles = {
    trial: {
      bg: 'bg-brand-50 border-brand-200',
      text: 'text-brand-700',
      icon: '🎉',
      message: `You're on a 14-day free trial. ${trialDaysLeft} days remaining.`,
      cta: 'Upgrade Now',
    },
    expiring: {
      bg: 'bg-warning-50 border-warning-200',
      text: 'text-warning-700',
      icon: '⚠️',
      message: `Your trial expires in ${trialDaysLeft} day${trialDaysLeft !== 1 ? 's' : ''}! Upgrade to keep your data.`,
      cta: 'Upgrade Now',
    },
    grace: {
      bg: 'bg-red-50 border-red-200',
      text: 'text-red-700',
      icon: '🔒',
      message: `Your subscription has expired. ${graceDaysLeft} days left to export data (read-only mode).`,
      cta: 'Renew Now',
    },
  };

  const style = bannerStyles[bannerType];

  return (
    <div className={`${style.bg} border-b px-4 py-2.5 flex items-center justify-center gap-3 text-sm ${style.text}`}>
      <span>{style.icon}</span>
      <span className="font-medium">{style.message}</span>
      <button
        onClick={() => navigate(allRoutes.planCheckout)}
        className="px-3 py-1 text-xs font-bold bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors"
      >
        {style.cta}
      </button>
      <button
        onClick={() => setDismissed(true)}
        className="ml-2 text-current opacity-50 hover:opacity-100 transition-opacity"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  );
};

export default SubscriptionBanner;
